import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus } from 'lucide-react';
import { useUnifiedTokens, UnifiedToken } from '../hooks/useUnifiedTokens';
import { useRelayPrice } from '../hooks/useRelayPrice';
import { formatMarketCap } from '../utils/address';
import CustomSwapWidget from '../components/swap/CustomSwapWidget';
import UnifiedCreateModal from '../components/shared/modals/UnifiedCreateModal';

// Brand Colors - Clean white theme with purple accent
const MAVRK_PURPLE = '#9535fc';
const MAVRK_DARK = '#333333';
const MAVRK_LIGHT = '#FFFFFF';
const INK_COLOR = '#9535fc';
const ETH_COLOR = '#627EEA';
const MONAD_COLOR = '#836EF9';

// ============= STYLED COMPONENTS =============

const PageContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  background: ${MAVRK_LIGHT};
  color: ${MAVRK_DARK};
  font-family: 'Inter', sans-serif;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  padding: 16px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: ${MAVRK_LIGHT};
  position: sticky;
  top: 0;
  z-index: 100;
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    flex-direction: column;
    gap: 12px;
  }
`;

const Logo = styled.a`
  font-size: 24px;
  font-weight: 800;
  color: ${MAVRK_DARK};
  text-decoration: none;
  letter-spacing: -0.5px;
  
  span {
    color: ${MAVRK_PURPLE};
  }
`;

const HeaderLinks = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
  
  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const HeaderLink = styled.a`
  color: #666666;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.2s ease;
  
  &:hover {
    color: ${MAVRK_PURPLE};
  }
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-bottom: 60px;
`;

const HeroSection = styled.section`
  padding: 60px 24px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: linear-gradient(180deg, rgba(149, 53, 252, 0.03) 0%, rgba(255, 255, 255, 0) 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 40px 16px 32px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 48px;
  font-weight: 800;
  color: ${MAVRK_DARK};
  margin: 0 0 16px 0;
  letter-spacing: -1px;
  
  span {
    color: ${MAVRK_PURPLE};
  }
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  color: #666666;
  max-width: 600px;
  margin: 0 0 32px 0;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

// Create Button
const CreateButton = styled.button<{ $color: string }>`
  background: ${props => props.$color};
  color: #FFFFFF;
  border: none;
  border-radius: 8px;
  padding: 14px 32px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  box-shadow: 0 4px 12px ${props => props.$color}40;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px ${props => props.$color}50;
    opacity: 0.9;
  }
`;

// Token List Section
const TokenListContainer = styled.div`
  background: transparent;
  padding: 32px 24px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 16px 12px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${MAVRK_DARK};
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

// Search & Filter
const SearchFilterRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 280px;
  background: #F5F5F5;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 12px 16px;
  color: ${MAVRK_DARK};
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
  
  &:focus {
    background: #FFFFFF;
    border-color: ${MAVRK_PURPLE};
    box-shadow: 0 0 0 2px rgba(149, 53, 252, 0.1);
  }

  &::placeholder {
    color: #999999;
  }
`;

const FilterButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ $active: boolean; $color?: string }>`
  background: ${props => props.$active ? (props.$color || MAVRK_PURPLE) : '#FFFFFF'};
  border: 1px solid ${props => props.$active ? (props.$color || MAVRK_PURPLE) : 'rgba(0, 0, 0, 0.15)'};
  border-radius: 8px;
  padding: 10px 16px;
  color: ${props => props.$active ? '#FFFFFF' : '#666666'};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    border-color: ${props => props.$color || MAVRK_PURPLE};
    color: ${props => props.$active ? '#FFFFFF' : (props.$color || MAVRK_PURPLE)};
  }
`;

const ChainDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$color};
`;

// Token Table
const TokenListTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 10px;
`;

const TokenListHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 40px 1fr 120px 100px 80px;
  gap: 12px;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 600;
  color: #666666;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  
  @media (max-width: 768px) {
    grid-template-columns: 30px 30px 1fr 60px 50px 45px;
    gap: 6px;
    padding: 10px 8px;
    font-size: 9px;
  }
`;

const TokenListRow = styled.div`
  display: grid;
  grid-template-columns: 40px 40px 1fr 120px 100px 80px;
  gap: 12px;
  padding: 16px;
  background: #FFFFFF;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  align-items: center;
  transition: all 0.2s;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
    border-color: ${MAVRK_PURPLE};
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 30px 30px 1fr 60px 50px 45px;
    gap: 6px;
    padding: 12px 8px;
  }
`;

const RankCell = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #999999;
  
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const ChainBadge = styled.div<{ $color: string }>`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${props => props.$color}15;
  border: 1px solid ${props => props.$color}30;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: ${props => props.$color};
  
  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
    font-size: 8px;
  }
`;

const TokenInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const TokenLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #FFFFFF;
  
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
`;

const TokenDetails = styled.div`
  min-width: 0;
`;

const TokenName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${MAVRK_DARK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const TokenSymbol = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #666666;
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const DataCell = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${MAVRK_DARK};
  text-align: right;
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const TradeButton = styled.button`
  background: ${MAVRK_LIGHT};
  border: 1px solid ${MAVRK_PURPLE};
  border-radius: 6px;
  color: ${MAVRK_PURPLE};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 6px 12px;
  
  &:hover {
    background: ${MAVRK_PURPLE};
    color: #FFFFFF;
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 4px 8px;
    font-size: 9px;
  }
`;

// Pagination
const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 32px;
  flex-wrap: wrap;
`;

const PaginationButton = styled.button<{ disabled?: boolean }>`
  background: ${props => props.disabled ? '#F5F5F5' : '#FFFFFF'};
  border: 1px solid ${props => props.disabled ? '#E0E0E0' : '#E0E0E0'};
  border-radius: 8px;
  color: ${props => props.disabled ? '#999999' : MAVRK_DARK};
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  padding: 8px 16px;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    border-color: ${MAVRK_PURPLE};
    color: ${MAVRK_PURPLE};
  }
`;

const PageInfo = styled.div`
  font-size: 14px;
  color: #999999;
`;

// Loading & Error states
const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666666;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #ef4444;
  font-size: 14px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999999;
  font-size: 16px;
`;

// Footer
const Footer = styled.footer`
  padding: 20px 32px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: ${MAVRK_LIGHT};
  text-align: center;
  color: #999999;
  font-size: 13px;
  
  a {
    color: ${MAVRK_PURPLE};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

// ============= HELPER FUNCTIONS =============

const ensureHttpsUrl = (url: string | null | undefined, fallback: string): string => {
  if (!url) return fallback;
  if (url.startsWith('Qm') && url.length === 46) {
    return `https://ipfs.io/ipfs/${url}`;
  }
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  return url;
};

const formatPriceWithSubscript = (price: number | null): React.ReactNode => {
  if (!price) return 'N/A';
  if (price >= 0.01) return `$${price.toFixed(6)}`;
  
  const priceStr = price.toFixed(20);
  const match = priceStr.match(/^0\.0*([1-9])/);
  
  if (match) {
    const zeros = priceStr.indexOf(match[1]) - 2;
    const significantDigits = priceStr.slice(priceStr.indexOf(match[1]), priceStr.indexOf(match[1]) + 2);
    return <>$0.0<sub>{zeros}</sub>{significantDigits}</>;
  }
  
  return `$${price.toFixed(6)}`;
};

const getChainLabel = (chain: string) => {
  switch (chain) {
    case 'ink': return 'INK';
    case 'ethereum': return 'ETH';
    case 'monad': return 'MON';
    default: return 'INK';
  }
};

const getChainColor = (chain: string) => {
  switch (chain) {
    case 'ink': return INK_COLOR;
    case 'ethereum': return ETH_COLOR;
    case 'monad': return MONAD_COLOR;
    default: return INK_COLOR;
  }
};

// Swap Modal Styles
const SwapModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const SwapModalContent = styled.div`
  background: #000000;
  border: 1px solid #333333;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const SwapModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #333333;
`;

const SwapModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

const SwapModalClose = styled.button`
  background: transparent;
  border: none;
  color: #71717a;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 18px;
  transition: all 0.2s ease;
  
  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.1);
  }
`;

// ============= MAIN COMPONENT =============

const UnifiedAppPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [chainFilter, setChainFilter] = useState<'all' | 'ink' | 'ethereum' | 'monad'>('all');
  
  // Create modal state (single unified modal)
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  
  // Trade modal state
  const [tradeModalOpen, setTradeModalOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState<UnifiedToken | null>(null);
  
  const {
    tokens,
    loading,
    error,
    currentPage,
    totalPages,
    goToNextPage,
    goToPreviousPage,
    refetch
  } = useUnifiedTokens(searchQuery, chainFilter);

  // Get prices for Ink tokens only (Relay supports Ink)
  const inkTokens = tokens.filter(t => t.chain === 'ink');
  const { getPriceForToken } = useRelayPrice(inkTokens);

  // Force white background
  React.useEffect(() => {
    const originalBackground = document.body.style.background;
    const originalColor = document.body.style.color;
    
    document.body.style.background = '#FFFFFF';
    document.body.style.color = '#333333';
    
    return () => {
      document.body.style.background = originalBackground;
      document.body.style.color = originalColor;
    };
  }, []);

  const handleTokenClick = (token: UnifiedToken) => {
    window.open(token.dexToolsUrl, '_blank');
  };

  const handleTradeClick = (e: React.MouseEvent, token: UnifiedToken) => {
    e.stopPropagation();
    
    if (token.chain === 'ink') {
      setSelectedToken(token);
      setTradeModalOpen(true);
    } else if (token.chain === 'ethereum') {
      window.open(token.tradeUrl, '_blank');
    } else if (token.chain === 'monad') {
      window.open(token.tradeUrl, '_blank');
    }
  };

  return (
    <PageContainer>
      <Header>
        <Logo href="/">
          <span>MAVRK</span>.XYZ
        </Logo>
        <HeaderLinks>
          <HeaderLink href="https://mavrk.ink" target="_blank">Ink</HeaderLink>
          <HeaderLink href="https://mavrk.ink/eth" target="_blank">Ethereum</HeaderLink>
          <HeaderLink href="https://mavrk.ink/monad" target="_blank">Monad</HeaderLink>
          <HeaderLink href="https://mavrk.ink/docs" target="_blank">Docs</HeaderLink>
        </HeaderLinks>
      </Header>

      <MainContent>
        <HeroSection>
          <HeroTitle>
            Deploy Tokens <span>Anywhere</span>
          </HeroTitle>
          <HeroSubtitle>
            Launch tokens across multiple chains with zero code. Ink, Ethereum, Monad — all in one place.
          </HeroSubtitle>

          <CreateButton $color={MAVRK_PURPLE} onClick={() => setIsCreateOpen(true)}>
            <Plus size={18} />
            Create Token
          </CreateButton>
        </HeroSection>

        <TokenListContainer>
          <SectionHeader>
            <SectionTitle>All Tokens</SectionTitle>
          </SectionHeader>

          <SearchFilterRow>
            <SearchInput
              type="text"
              placeholder="Search by name, symbol, or contract address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <FilterButtons>
              <FilterButton 
                $active={chainFilter === 'all'}
                onClick={() => setChainFilter('all')}
              >
                All
              </FilterButton>
              <FilterButton 
                $active={chainFilter === 'ink'}
                $color={INK_COLOR}
                onClick={() => setChainFilter('ink')}
              >
                <ChainDot $color={INK_COLOR} />
                Ink
              </FilterButton>
              <FilterButton 
                $active={chainFilter === 'ethereum'}
                $color={ETH_COLOR}
                onClick={() => setChainFilter('ethereum')}
              >
                <ChainDot $color={ETH_COLOR} />
                Ethereum
              </FilterButton>
              <FilterButton 
                $active={chainFilter === 'monad'}
                $color={MONAD_COLOR}
                onClick={() => setChainFilter('monad')}
              >
                <ChainDot $color={MONAD_COLOR} />
                Monad
              </FilterButton>
            </FilterButtons>
          </SearchFilterRow>

          {loading ? (
            <LoadingMessage>Loading tokens...</LoadingMessage>
          ) : error ? (
            <ErrorMessage>Error: {error}</ErrorMessage>
          ) : tokens.length === 0 ? (
            <EmptyMessage>
              {searchQuery ? 'No tokens found matching your search.' : 'No tokens deployed yet.'}
            </EmptyMessage>
          ) : (
            <>
              <TokenListTable>
                <TokenListHeader>
                  <div>#</div>
                  <div>Chain</div>
                  <div>Token</div>
                  <div style={{ textAlign: 'right' }}>Market Cap</div>
                  <div style={{ textAlign: 'right' }}>Price</div>
                  <div style={{ textAlign: 'center' }}>Trade</div>
                </TokenListHeader>

                {tokens.map((token, index) => {
                  const priceUSD = token.chain === 'ink' && token.contract_address 
                    ? getPriceForToken(token.contract_address) 
                    : null;
                  const marketCap = priceUSD ? formatMarketCap(priceUSD) : 'N/A';

                  return (
                    <TokenListRow 
                      key={`${token.chain}-${token.contract_address}`} 
                      onClick={() => handleTokenClick(token)}
                    >
                      <RankCell>{index + 1 + (currentPage - 1) * 20}</RankCell>
                      <ChainBadge $color={getChainColor(token.chain)}>
                        {getChainLabel(token.chain)}
                      </ChainBadge>
                      <TokenInfo>
                        <TokenLogo 
                          src={ensureHttpsUrl(token.logo_url, 'https://placehold.co/32x32/f5f5f5/999?text=?')}
                          alt={token.name}
                        />
                        <TokenDetails>
                          <TokenName>{token.name}</TokenName>
                          <TokenSymbol>${token.symbol}</TokenSymbol>
                        </TokenDetails>
                      </TokenInfo>
                      <DataCell>{marketCap}</DataCell>
                      <DataCell>{formatPriceWithSubscript(priceUSD)}</DataCell>
                      <DataCell>
                        <TradeButton onClick={(e) => handleTradeClick(e, token)}>
                          Trade
                        </TradeButton>
                      </DataCell>
                    </TokenListRow>
                  );
                })}
              </TokenListTable>

              {totalPages > 1 && (
                <PaginationContainer>
                  <PaginationButton
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                  >
                    ← Previous
                  </PaginationButton>
                  <PageInfo>Page {currentPage} of {totalPages}</PageInfo>
                  <PaginationButton
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                  >
                    Next →
                  </PaginationButton>
                </PaginationContainer>
              )}
            </>
          )}
        </TokenListContainer>
      </MainContent>

      <Footer>
        Powered by <a href="https://mavrk.ink" target="_blank" rel="noopener noreferrer">Mavrk</a> — 
        Deploy tokens with zero code across multiple chains
      </Footer>

      {/* Unified Create Modal */}
      <UnifiedCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSuccess={() => {
          setIsCreateOpen(false);
          setTimeout(refetch, 500);
        }}
      />

      {/* Swap Modal */}
      {tradeModalOpen && selectedToken && (
        <SwapModalOverlay onClick={() => { setTradeModalOpen(false); setSelectedToken(null); }}>
          <SwapModalContent onClick={(e) => e.stopPropagation()}>
            <SwapModalHeader>
              <SwapModalTitle>Trade {selectedToken.symbol}</SwapModalTitle>
              <SwapModalClose onClick={() => { setTradeModalOpen(false); setSelectedToken(null); }}>
                ✕
              </SwapModalClose>
            </SwapModalHeader>
            <CustomSwapWidget
              preselectedToToken={{
                address: selectedToken.contract_address || '',
                symbol: selectedToken.symbol,
                name: selectedToken.name,
                chainId: selectedToken.chainId,
                logo: selectedToken.logo_url
              }}
              embedded={true}
            />
          </SwapModalContent>
        </SwapModalOverlay>
      )}
    </PageContainer>
  );
};

export default UnifiedAppPage;
